import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const UsagePatternChart = ({ analyticsData, dateRange }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Select first product by default
  React.useEffect(() => {
    if (analyticsData?.length > 0 && !selectedProduct) {
      setSelectedProduct(analyticsData?.[0]);
    }
  }, [analyticsData, selectedProduct]);

  // Generate daily usage pattern data for selected product
  const generateUsageData = () => {
    if (!selectedProduct) return [];

    const days = parseInt(dateRange) || 30;
    const avgDailyUsage = parseFloat(selectedProduct?.avg_daily_usage) || 0;
    const data = [];

    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date?.setDate(date?.getDate() - i);
      
      // Simulate usage pattern with some variance
      const variance = (Math.random() - 0.5) * avgDailyUsage * 0.4;
      const usage = Math.max(0, avgDailyUsage + variance);
      
      data?.push({
        date: date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        usage: parseFloat(usage?.toFixed(2)),
        stockLevel: Math.max(0, selectedProduct?.quantity - (days - i) * avgDailyUsage)
      });
    }

    return data;
  };

  const usageData = generateUsageData();

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-elevation-2">
          <p className="font-medium text-foreground mb-2">{payload?.[0]?.payload?.date}</p>
          <p className="text-sm text-primary">Daily Usage: {payload?.[0]?.value}</p>
          {payload?.[1] && (
            <p className="text-sm text-success">Stock Level: {payload?.[1]?.value?.toFixed(0)}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-1">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground mb-1">
            Usage Patterns by Product
          </h2>
          <p className="text-sm text-muted-foreground">
            Daily consumption trends and stock level projection
          </p>
        </div>
        <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
          <Icon name="Activity" size={20} color="var(--color-success)" />
        </div>
      </div>
      {/* Product Selector */}
      <div className="mb-4">
        <label className="text-sm font-medium text-foreground mb-2 block">Select Product:</label>
        <select
          value={selectedProduct?.id || ''}
          onChange={(e) => {
            const product = analyticsData?.find(p => p?.id === e?.target?.value);
            setSelectedProduct(product);
          }}
          className="w-full md:w-96 px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {analyticsData?.map(product => (
            <option key={product?.id} value={product?.id}>
              {product?.name} ({product?.sku})
            </option>
          ))}
        </select>
      </div>
      {/* Product Stats */}
      {selectedProduct && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-background rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Current Stock</p>
            <p className="text-lg font-semibold text-foreground">{selectedProduct?.quantity}</p>
          </div>
          <div className="bg-background rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Avg Daily Usage</p>
            <p className="text-lg font-semibold text-primary">{parseFloat(selectedProduct?.avg_daily_usage || 0)?.toFixed(2)}</p>
          </div>
          <div className="bg-background rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Reorder Level</p>
            <p className="text-lg font-semibold text-warning">{selectedProduct?.reorder_level}</p>
          </div>
          <div className="bg-background rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Days Until Reorder</p>
            <p className="text-lg font-semibold text-error">
              {selectedProduct?.days_until_reorder !== null ? selectedProduct?.days_until_reorder : 'N/A'}
            </p>
          </div>
        </div>
      )}
      {usageData?.length === 0 ? (
        <div className="h-80 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Activity" size={48} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">No usage data available</p>
          </div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={usageData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="date" 
              tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
              interval="preserveStartEnd"
            />
            <YAxis 
              tick={{ fill: 'var(--color-muted-foreground)' }}
              label={{ value: 'Quantity', angle: -90, position: 'insideLeft', fill: 'var(--color-muted-foreground)' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />
            <Line 
              type="monotone" 
              dataKey="usage" 
              stroke="var(--color-primary)" 
              strokeWidth={2}
              name="Daily Usage"
              dot={{ fill: 'var(--color-primary)', r: 3 }}
            />
            <Line 
              type="monotone" 
              dataKey="stockLevel" 
              stroke="var(--color-success)" 
              strokeWidth={2}
              name="Projected Stock Level"
              dot={false}
              strokeDasharray="5 5"
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default UsagePatternChart;